import { useEffect, useState } from 'react'
import { Alert, Modal } from 'medere-ui-framework'
import { Calendar } from 'primereact/calendar'
import { ContractHonorariumListItemDTO } from '../../../dto/contractHonorarium/ContractHonorariumDTO'
import { GENERAL_LABEL } from '../contractHonorariumFormat'

interface CloseContractHonorariumModalProps {
  visible: boolean
  contractHonorarium: ContractHonorariumListItemDTO | null
  confirming: boolean
  apiError?: string
  onCancel: () => void
  onConfirm: (closeDate: Date) => void
}

const CLOSE_DATE_REQUIRED = 'Completá la fecha de cierre'
const CLOSE_DATE_BEFORE_TODAY = 'Ingresá una fecha de cierre igual o posterior a hoy'

function today(): Date {
  const now = new Date()
  return new Date(now.getFullYear(), now.getMonth(), now.getDate())
}

function isGeneral(value: string): boolean {
  return !value || value === '(General)'
}

function orGeneral(value: string): string {
  return isGeneral(value) ? GENERAL_LABEL : value
}

function describeHealthEntity(row: ContractHonorariumListItemDTO): string {
  return `${orGeneral(row.healthentityname)} - ${orGeneral(row.healthentityplanname)}`
}

function ReadOnlyData({ label, value }: Readonly<{ label: string; value: string }>) {
  return (
    <div className='md-readonly'>
      <span className='md-readonly__label'>{label}</span>
      <span className='md-readonly__value'>{value}</span>
    </div>
  )
}

function validate(closeDate: Date | null): string | undefined {
  if (!closeDate) return CLOSE_DATE_REQUIRED
  if (closeDate < today()) return CLOSE_DATE_BEFORE_TODAY
  return undefined
}

export default function CloseContractHonorariumModal({
  visible,
  contractHonorarium,
  confirming,
  apiError,
  onCancel,
  onConfirm,
}: Readonly<CloseContractHonorariumModalProps>) {
  const [closeDate, setCloseDate] = useState<Date | null>(today)
  const [error, setError] = useState<string | undefined>()

  useEffect(() => {
    if (!visible) return
    setCloseDate(today())
    setError(undefined)
    const timer = window.setTimeout(() => {
      const cancelButton = document.querySelector('.chd-close-modal .modalFooter .buttonMain3')
      if (cancelButton instanceof HTMLElement) cancelButton.focus()
    }, 50)
    return () => window.clearTimeout(timer)
  }, [visible])

  useEffect(() => {
    setError(apiError)
  }, [apiError])

  function confirm() {
    const found = validate(closeDate)
    setError(found)
    if (!found && closeDate) onConfirm(closeDate)
  }

  return (
    <Modal
      visible={visible}
      setVisible={(nextVisible) => {
        if (!nextVisible) onCancel()
      }}
      header={<span className='chd-modal-title'>Agregar fecha de cierre</span>}
      width='w-medium'
      className='chd-modal chd-close-modal'
      footerButtonLeftText='Cancelar'
      onClickLeftBtn={onCancel}
      footerButtonRightText='Agregar fecha de cierre'
      onClickRightBtn={confirm}
      rightButtonDisabled={confirming}
    >
      {contractHonorarium && (
        <div className='chd-modal-body'>
          <div className='chd-close-data'>
            <ReadOnlyData label='Emisor de facturación' value={orGeneral(contractHonorarium.billingentityname)} />
            <ReadOnlyData label='Profesional' value={orGeneral(contractHonorarium.professional)} />
            <ReadOnlyData label='Obra social' value={describeHealthEntity(contractHonorarium)} />
            <ReadOnlyData label='Nomenclador' value={orGeneral(contractHonorarium.nomenclator)} />
            <ReadOnlyData label='Edificio' value={orGeneral(contractHonorarium.buildingname)} />
          </div>
          <div className='chd-field'>
            <label htmlFor='chd-close-date'>
              Fecha de cierre <span className='md-req'>*</span>
            </label>
            <Calendar
              inputId='chd-close-date'
              value={closeDate}
              dateFormat='dd/mm/yy'
              showIcon
              minDate={today()}
              placeholder='dd/mm/aaaa'
              appendTo={document.body}
              className={error ? 'p-invalid' : ''}
              onChange={(event) => {
                const value = event.value instanceof Date ? event.value : null
                setCloseDate(value)
                if (error) setError(validate(value))
              }}
              aria-invalid={error ? true : undefined}
              aria-describedby={error ? 'chd-close-date-error' : undefined}
            />
            {error && (
              <small id='chd-close-date-error' className='chd-field-error'>
                <i className='pi pi-exclamation-triangle' aria-hidden='true' />
                {error}
              </small>
            )}
          </div>
        </div>
      )}
    </Modal>
  )
}
